// src/pages/ManuscriptTheme.jsx
//
// The Theme Editor: /manuscript/<id>/theme, reached from "Customize theme" in the
// editor. The look of one manuscript — its typefaces, margins, chapter openings,
// ornaments — edited with the whole book laid out beside the controls.
//
// **A manuscript theme, not `src/theme.js`.** The page's own chrome comes from
// the app's tokens; the theme being edited is the author's data, shown in the
// preview and nowhere else.
//
// Built to the Theme Editor prototype, with the ticket's changes: it is a page
// rather than an overlay, so the app's Navbar stays (the route is not in
// OWN_NAV_ROUTES); the prototype's theme dropdown is gone (switching lives in the
// editor's format bar); and its primary action is "Apply to Manuscript".
//
// **What Apply does.** The manuscript gets a copy of the theme (`manuscriptTheme`
// on the draft), never a link — see backend/routes/manuscript_themes.py. And a
// theme that is not a preset as it ships is saved to the author's own themes
// first, which is what needs a name: an unchanged preset applies as itself, a
// changed one asks for a name and becomes the author's, and one of the author's
// own themes is updated in place. Only the author may apply; the API refuses
// anyone else, and the page says so up front.
//
// Nothing is written until Apply. Leaving with changes asks first.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, IconButton, Menu, MenuItem, Snackbar, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { tokens } from '../theme';
import { API_BASE } from '../utils/api';
import { DEFAULT_BOOK_SIZE, bookSize } from '../config/bookSizes';
import { ART_SAMPLES, presetById } from '../config/manuscriptThemes';
import { completeTheme, sameLook, themeCss, themeValues } from '../utils/manuscriptTheme';
import {
  createSavedTheme, deleteSavedTheme, errorCode, listSavedThemes, setManuscriptTheme, updateSavedTheme, uploadThemeArt,
} from '../utils/manuscriptThemeApi';
import Dialog from '../components/common/Dialog';
import Field from '../components/common/Field';
import ConfirmModal from '../components/editor/ConfirmModal';
import ThemeControlPanel from '../components/manuscriptTheme/ThemeControlPanel';
import ThemePreview from '../components/manuscriptTheme/ThemePreview';
import { SECTIONS } from '../components/manuscriptTheme/themeSections';

const C = tokens.color;
const PANEL_W = 372;
const PANEL_FLOATS = '@media (max-width: 900px)';

/** A working theme: complete values, plus the name and where it came from. */
const asWork = (theme, sourceId) => ({
  ...completeTheme(theme),
  name: theme?.name || '',
  sourceId: sourceId ?? theme?.sourceId ?? null,
});

const DEFAULT_START = () => asWork(presetById('literary'), 'literary');

function setPath(obj, path, value) {
  const [section, key] = path.split('.');
  return { ...obj, [section]: { ...obj[section], [key]: value } };
}

export default function ManuscriptTheme() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const userId = user?._id || user?.id || null;

  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saved, setSaved] = useState([]);
  const [applied, setApplied] = useState(null);   // the manuscript's copy, or null
  const [work, setWork] = useState(DEFAULT_START);
  const [openId, setOpenId] = useState('chapter');
  const [layoutInfo, setLayoutInfo] = useState({ pages: 0, chapters: 0 });
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadingArt, setUploadingArt] = useState(false);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [namePrompt, setNamePrompt] = useState(null);   // { suggestion } | null
  const [nameDraft, setNameDraft] = useState('');
  const [leaveTo, setLeaveTo] = useState(null);        // where a guarded exit was headed
  const [panelOpen, setPanelOpen] = useState(false);   // only at 900px and below
  const [topbar, setTopbar] = useState(64);
  const importRef = useRef(null);

  // ---- load: the manuscript (and its theme), then the author's themes -------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('rv_access_token');
        const res = await fetch(`${API_BASE}/api/drafts/${id}`, {
          credentials: 'omit',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          throw new Error(res.status === 403
            ? 'You do not have access to this manuscript.'
            : 'This manuscript could not be loaded.');
        }
        const data = await res.json();
        if (cancelled) return;
        setDraft(data);
        if (data.manuscriptTheme) {
          const copy = asWork(data.manuscriptTheme);
          setApplied(copy);
          setWork(copy);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'This manuscript could not be loaded.');
      }
    })();
    listSavedThemes()
      .then((list) => { if (!cancelled && Array.isArray(list)) setSaved(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // The page fills the window under the Navbar, like the editor.
  useLayoutEffect(() => {
    const header = document.querySelector('.global-header') || document.querySelector('header');
    if (header) setTopbar(header.getBoundingClientRect().height);
  }, []);

  const trimSize = draft?.trimSize || DEFAULT_BOOK_SIZE;
  const trimInches = { w: bookSize(trimSize).widthIn, h: bookSize(trimSize).heightIn };

  const isAuthor = Boolean(draft && userId
    && String(draft.author_id || draft.author?._id || draft.author?.id || '') === String(userId));

  // Changed from where the page started: the manuscript's own theme, or
  // Literary Classic on a manuscript that has none. Opening the page and
  // leaving again is not a change, whatever the manuscript has.
  const startTheme = useMemo(DEFAULT_START, []);
  const baseline = applied || startTheme;
  const dirty = useMemo(() => !sameLook(work, baseline)
    || (work.name || '') !== (baseline.name || ''), [work, baseline]);

  // ---- leaving with unapplied changes --------------------------------------
  useEffect(() => {
    if (!dirty || !draft) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    // The Navbar's links leave the page too. Catch them before the router does.
    const onClick = (e) => {
      const a = e.target.closest?.('a[href]');
      if (!a || a.target === '_blank' || e.defaultPrevented) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaveTo(url.pathname + url.search);
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, draft]);

  const backToEditor = `/editor/${id}`;
  const close = () => (dirty && isAuthor ? setLeaveTo(backToEditor) : navigate(backToEditor));

  // ---- editing -------------------------------------------------------------
  const set = useCallback((path, value) => setWork((w) => setPath(w, path, value)), []);

  const pickTheme = (theme, sourceId) => {
    setWork(asWork(theme, sourceId));
    setToast(`Previewing ${theme.name} — click Apply to Manuscript to keep it`);
  };

  const revert = () => {
    setWork(baseline);
    setToast(applied ? 'Reverted to the theme the manuscript uses' : 'Reverted to Literary Classic');
  };

  const toggleSection = (sid) => setOpenId((cur) => (cur === sid ? null : sid));
  const focus = SECTIONS.find((s) => s.id === openId)?.focus || null;

  // ---- page art ------------------------------------------------------------
  // Picking a sample places it the way the prototype suggests for that image.
  const artActions = {
    uploading: uploadingArt,
    pick: (id) => {
      const sample = ART_SAMPLES[id];
      setWork((w) => ({
        ...w,
        art: { ...w.art, ...sample.suggest, on: true, src: `sample:${id}`, name: sample.name, w: sample.w, h: sample.h },
      }));
      setToast(`${sample.name} placed with recommended settings — fine-tune below`);
    },
    upload: async (file) => {
      setUploadingArt(true);
      try {
        const up = await uploadThemeArt(file);
        if (!up) return;
        setWork((w) => {
          const pages = Object.values(w.art.pages).some(Boolean) ? w.art.pages : { ...w.art.pages, opener: true };
          return { ...w, art: { ...w.art, on: true, src: up.src, name: up.name, w: up.w, h: up.h, pages } };
        });
        setToast(`Uploaded ${up.name} (${up.w}×${up.h}px)`);
      } catch (err) {
        const code = errorCode(err);
        setToast(code === 'too_large' ? 'That image is over 15 MB.'
          : code === 'unsupported_type' ? 'Page art can be a PNG, JPG or WebP image.'
            : code === 'unreadable' ? 'That file could not be read as an image.'
              : 'The image could not be uploaded. Please try again.');
      } finally {
        setUploadingArt(false);
      }
    },
    remove: () => setWork((w) => ({ ...w, art: { ...w.art, on: false, src: '', name: '', w: 0, h: 0 } })),
  };

  const deleteSaved = async (theme) => {
    try {
      await deleteSavedTheme(theme.id);
      setSaved((list) => list.filter((t) => t.id !== theme.id));
      // The working theme keeps its values; it just no longer has a home.
      if (work.sourceId === theme.id) setWork((w) => ({ ...w, sourceId: null }));
      setToast(`Deleted ${theme.name}. Manuscripts that use it keep their copy.`);
    } catch {
      setToast('That theme could not be deleted. Please try again.');
    }
  };

  // ---- export / import -----------------------------------------------------
  const download = () => {
    const blob = new Blob([JSON.stringify({ name: work.name, ...themeValues(work) }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(work.name || 'theme').replace(/[^\w]+/g, '-').toLowerCase()}.revisionari-theme.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    setToast('Theme downloaded');
  };

  const copyCss = async () => {
    const css = themeCss(work, { scope: '.rv-book' });
    try {
      await navigator.clipboard.writeText(css);
      setToast('Stylesheet copied — it is scoped under .rv-book');
    } catch {
      setToast('The stylesheet could not be copied');
    }
  };

  // An imported theme becomes one of the author's own straight away, as the
  // prototype's import does, and is loaded into the editor to look at.
  const importFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!parsed || typeof parsed !== 'object') throw new Error('not a theme');
        const name = String(parsed.name || 'Imported theme').slice(0, 80);
        const created = await createSavedTheme(name, completeTheme(parsed));
        setSaved((list) => [...list, created]);
        setWork(asWork(created, created.id));
        setToast(`Imported ${created.name}`);
      } catch (err) {
        setToast(errorCode(err) === 'too_many_themes'
          ? 'You have the most themes you can keep. Delete one to import another.'
          : 'That file is not a ReVisionari theme');
      }
    };
    reader.readAsText(file);
  };

  // ---- apply ---------------------------------------------------------------
  const apply = async (givenName) => {
    if (!isAuthor || busy) return;
    const name = (givenName ?? work.name ?? '').trim();
    const preset = presetById(work.sourceId);
    const own = saved.find((t) => t.id === work.sourceId);
    const changedPreset = preset && !sameLook(work, preset);

    // Saving a theme of the author's own needs a name they chose.
    if (givenName === undefined && ((changedPreset && (!name || name === preset.name)) || (!preset && !own && !name))) {
      const suggestion = changedPreset ? `${preset.name} – mine` : (name || '');
      setNameDraft(suggestion);
      setNamePrompt({ suggestion });
      return;
    }

    setBusy(true);
    try {
      let sourceId = work.sourceId;
      let finalName = name || preset?.name || own?.name || 'Untitled theme';
      if (changedPreset || (!preset && !own)) {
        const created = await createSavedTheme(finalName, work);
        sourceId = created.id;
        finalName = created.name;
        setSaved((list) => [...list, created]);
      } else if (own) {
        const updated = await updateSavedTheme(own.id, finalName, work);
        finalName = updated.name;
        setSaved((list) => list.map((t) => (t.id === own.id ? updated : t)));
      }
      await setManuscriptTheme(id, work, { name: finalName, sourceId });
      const copy = { ...work, name: finalName, sourceId };
      setApplied(copy);
      setWork(copy);
      // Back to the manuscript, which shows the theme and offers to clear any
      // old formatting that would hide it — that needs the live pages.
      navigate(backToEditor, { state: { themeApplied: finalName } });
    } catch (err) {
      const code = errorCode(err);
      setToast(code === 'not_author' ? 'Only the author can change this manuscript’s theme.'
        : code === 'too_many_themes' ? 'You have the most themes you can keep. Delete one in Theme presets first.'
          : code === 'name_required' ? 'Give the theme a name.'
            : 'The theme could not be applied. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ---- render --------------------------------------------------------------
  const pageHeight = `calc(100vh - ${topbar}px)`;

  if (loadError) {
    return (
      <Box sx={{ minHeight: pageHeight, display: 'grid', placeItems: 'center', p: 3, background: C.page }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 16, color: C.ink, mb: 2 }}>{loadError}</Typography>
          <Button variant="outlined" onClick={() => navigate('/library')}>Back to the Library</Button>
        </Box>
      </Box>
    );
  }
  if (!draft) {
    return (
      <Box sx={{ minHeight: pageHeight, display: 'grid', placeItems: 'center', background: C.page }}>
        <CircularProgress sx={{ color: C.purple }} />
      </Box>
    );
  }
  if (!isAuthor) {
    return (
      <Box sx={{ minHeight: pageHeight, display: 'grid', placeItems: 'center', p: 3, background: C.page }}>
        <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
          <Typography sx={{ fontSize: 16, color: C.ink, mb: 1 }}>Only this manuscript’s author can change its theme.</Typography>
          <Typography sx={{ fontSize: 13, color: C.muted, mb: 2.5 }}>
            {applied ? `It is set in ${applied.name}.` : 'It uses its own formatting.'}
          </Typography>
          <Button variant="outlined" onClick={() => navigate(backToEditor)}>Back to the manuscript</Button>
        </Box>
      </Box>
    );
  }

  const book = {
    title: draft.title || 'Untitled',
    author: draft.author?.username || '',
    surname: (draft.author?.username || '').trim().split(/\s+/).pop() || '',
  };

  const btnSx = {
    textTransform: 'none', fontWeight: 600, fontSize: 13.5, borderRadius: `${tokens.radius.panel}px`,
    px: 1.75, whiteSpace: 'nowrap',
  };

  return (
    <Box sx={{ height: pageHeight, minHeight: 480, display: 'flex', flexDirection: 'column', background: C.plumInk }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          minHeight: 66, flexShrink: 0, background: C.card, display: 'flex', alignItems: 'center',
          flexWrap: 'wrap', gap: 2, px: 2.25, py: 1, borderBottom: `1px solid ${C.line}`,
        }}
      >
        <IconButton aria-label="Close the Theme Editor" onClick={close} sx={{ border: `1px solid ${C.line}`, borderRadius: `${tokens.radius.panel}px` }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="Theme settings"
          onClick={() => setPanelOpen((v) => !v)}
          sx={{ display: 'none', [PANEL_FLOATS]: { display: 'inline-flex' }, border: `1px solid ${C.line}`, borderRadius: `${tokens.radius.panel}px` }}
        >
          <TuneIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.14em', color: C.purple, textTransform: 'uppercase' }}>
            Theme editor
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              variant="standard"
              value={work.name}
              placeholder="Name this theme"
              onChange={(e) => setWork((w) => ({ ...w, name: e.target.value.slice(0, 80) }))}
              inputProps={{ 'aria-label': 'Theme name' }}
              InputProps={{ disableUnderline: true, sx: { fontSize: 18, fontWeight: 700, color: C.ink, width: { xs: 180, sm: 260 } } }}
            />
            {dirty && (
              <Box sx={{ fontSize: 11, fontWeight: 600, color: C.warning, background: C.warningTint, borderRadius: `${tokens.radius.base}px`, px: 1, py: 0.25, whiteSpace: 'nowrap' }}>
                Unapplied changes
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{
          mx: 'auto', display: 'flex', alignItems: 'center', gap: 1, fontSize: 12.5, color: C.muted,
          background: C.purpleTint, border: `1px solid ${C.purpleTintDeep}`, borderRadius: 999, px: 1.75, py: 0.75,
          whiteSpace: 'nowrap', '@media (max-width: 1180px)': { display: 'none' },
        }}
        >
          <MenuBookOutlinedIcon sx={{ fontSize: 16 }} />
          <span>
            Applies to the <b style={{ color: C.purple }}>whole manuscript</b>
            {layoutInfo.pages ? ` · ${layoutInfo.chapters} chapters · ${layoutInfo.pages} pages` : ''}
          </span>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={(e) => setExportAnchor(e.currentTarget)} endIcon={<KeyboardArrowDownIcon />} sx={{ ...btnSx, color: C.plum, borderColor: C.line }}>
            Export
          </Button>
          <Button variant="outlined" onClick={revert} sx={{ ...btnSx, color: C.plum, borderColor: C.line }}>Revert</Button>
          <Button
            variant="contained" onClick={() => apply()} disabled={busy}
            sx={{ ...btnSx, background: C.purple, '&:hover': { background: C.purpleDeep } }}
          >
            {busy ? 'Applying…' : 'Apply to Manuscript'}
          </Button>
        </Box>
      </Box>

      <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
        <MenuItem onClick={() => { setExportAnchor(null); download(); }}>Download theme (.json)</MenuItem>
        <MenuItem onClick={() => { setExportAnchor(null); copyCss(); }}>Copy stylesheet (CSS)</MenuItem>
        <MenuItem onClick={() => { setExportAnchor(null); importRef.current?.click(); }}>Import theme (.json)…</MenuItem>
      </Menu>
      <input ref={importRef} type="file" accept="application/json,.json" hidden onChange={importFile} />

      {/* Controls · preview */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
        <Box
          sx={{
            width: PANEL_W, flexShrink: 0, background: C.card, overflowY: 'auto', borderRight: `1px solid ${C.line}`,
            [PANEL_FLOATS]: {
              position: 'absolute', zIndex: tokens.z.raised, top: 0, bottom: 0, left: 0,
              width: `min(${PANEL_W}px, 100%)`, boxShadow: tokens.shadow.lg,
              display: panelOpen ? 'block' : 'none',
            },
          }}
        >
          <ThemeControlPanel
            work={work}
            saved={saved}
            openId={openId}
            onToggle={toggleSection}
            set={set}
            onPickPreset={pickTheme}
            onDeleteSaved={deleteSaved}
            onImport={() => importRef.current?.click()}
            art={artActions}
            trimInches={trimInches}
          />
        </Box>
        <ThemePreview
          html={draft.content || ''}
          theme={work}
          trimSize={trimSize}
          book={book}
          focus={focus}
          onLayout={setLayoutInfo}
        />
      </Box>

      {/* Naming a theme of the author's own, the first time it is saved */}
      <Dialog
        open={Boolean(namePrompt)}
        onClose={() => setNamePrompt(null)}
        title="Name this theme"
        width={420}
        actions={(
          <>
            <Button onClick={() => setNamePrompt(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!nameDraft.trim()}
              onClick={() => { setNamePrompt(null); setWork((w) => ({ ...w, name: nameDraft.trim() })); apply(nameDraft.trim()); }}
              sx={{ textTransform: 'none', background: C.purple }}
            >
              Save and apply
            </Button>
          </>
        )}
      >
        <Typography sx={{ fontSize: 13.5, color: C.muted, mb: 2 }}>
          Your changes become a theme of your own, which you can use on any manuscript.
        </Typography>
        <Field
          autoFocus
          fullWidth
          label="Theme name"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value.slice(0, 80))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && nameDraft.trim()) {
              setNamePrompt(null);
              setWork((w) => ({ ...w, name: nameDraft.trim() }));
              apply(nameDraft.trim());
            }
          }}
        />
      </Dialog>

      <ConfirmModal
        open={Boolean(leaveTo)}
        title="Discard the unapplied theme changes?"
        message="Nothing has been applied to the manuscript yet."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        destructive
        onConfirm={() => { const to = leaveTo; setLeaveTo(null); navigate(to); }}
        onCancel={() => setLeaveTo(null)}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3200}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={toast}
      />
    </Box>
  );
}
